import { useState } from 'react';
import { Check, Pencil, RefreshCw, SmilePlus, Trash2, X } from 'lucide-react';
import type { ContactMessage } from '../../app/legacy-types';

const REACTIONS = ['👍', '❤️', '🔥', '👏', '😂', '😮'];

export type FailedMessageAction = {
  id: string;
  messageId: string;
  actionType: 'EDIT' | 'DELETE_FOR_EVERYONE' | 'REACTION' | string;
  error?: string | null;
  attempts?: number;
};

export type ContactActivity = {
  id: string;
  message_id?: string | null;
  action: string;
  details?: Record<string, unknown> | null;
  created_at: string;
  user_id?: string | null;
  user_name?: string | null;
  message_action_id?: string | null;
  message_action_status?: string | null;
  message_action_type?: string | null;
  message_action_error?: string | null;
  message_action_attempts?: number | null;
};

export function failedMessageAction(activity: ContactActivity[], messageId: string): FailedMessageAction | undefined {
  const item = activity.find(row => row.message_id === messageId && row.message_action_status === 'FAILED' && row.message_action_id);
  if (!item?.message_action_id) return undefined;
  return {
    id: item.message_action_id,
    messageId,
    actionType: item.message_action_type || String(item.details?.actionType || ''),
    error: item.message_action_error || String(item.details?.error || ''),
    attempts: Number(item.message_action_attempts || 0),
  };
}

function notExpired(value?: string | null): boolean {
  if (!value) return true;
  const timestamp = new Date(value).getTime();
  return !Number.isFinite(timestamp) || timestamp > Date.now();
}

export function MessageActions(props: {
  message: ContactMessage;
  failedAction?: FailedMessageAction;
  onEdit: (messageId: string, body: string) => Promise<unknown> | unknown;
  onDelete: (messageId: string) => Promise<unknown> | unknown;
  onReact: (messageId: string, reaction: string) => Promise<unknown> | unknown;
  onRetry: (actionId: string) => Promise<unknown> | unknown;
}) {
  const [editing, setEditing] = useState(false);
  const [reactionOpen, setReactionOpen] = useState(false);
  const [draft, setDraft] = useState(props.message.body);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState('');
  const capabilities = props.message.capabilities;
  const canEdit = Boolean(capabilities?.canEdit && notExpired(capabilities.editExpiresAt) && !props.message.deleted_for_everyone_at);
  const canDelete = Boolean(capabilities?.canDeleteForEveryone && notExpired(capabilities.deleteExpiresAt) && !props.message.deleted_for_everyone_at);
  const canReact = Boolean(capabilities?.canReact && notExpired(capabilities.actionExpiresAt) && !props.message.deleted_for_everyone_at);
  const reactions = Array.isArray(props.message.reactions) ? props.message.reactions : [];

  async function run(action: () => Promise<unknown> | unknown, success?: () => void) {
    setPending(true);
    setError('');
    try {
      await action();
      success?.();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Не удалось выполнить действие');
    } finally {
      setPending(false);
    }
  }

  if (!canEdit && !canDelete && !canReact && !props.failedAction && !reactions.length && !editing) return null;
  return <div className="contact-message-actions">
    {reactions.length > 0 && <div className="contact-message-reactions" aria-label="Реакции на сообщение">{reactions.map((reaction, index) => <span key={`${String(reaction.emoji || '')}:${index}`}>{String(reaction.emoji || '')}</span>)}</div>}
    {!editing && (canEdit || canDelete || canReact) && <div className="contact-message-actions-toolbar">
      {canEdit && <button type="button" className="contact-message-action-button" aria-label="Редактировать сообщение" title="Редактировать" onClick={() => { setDraft(props.message.body); setEditing(true); setError(''); }} disabled={pending}><Pencil /></button>}
      {canReact && <button type="button" className="contact-message-action-button" aria-label="Добавить реакцию" title="Добавить реакцию" onClick={() => setReactionOpen(value => !value)} disabled={pending}><SmilePlus /></button>}
      {canDelete && <button type="button" className="contact-message-action-button danger" aria-label="Удалить сообщение у всех" title="Удалить у всех" onClick={() => { if (window.confirm('Удалить сообщение у всех участников?')) void run(() => props.onDelete(props.message.id)); }} disabled={pending}><Trash2 /></button>}
    </div>}
    {reactionOpen && <div className="contact-reaction-picker" role="group" aria-label="Выберите реакцию">{REACTIONS.map(reaction => <button key={reaction} type="button" aria-label={`Реакция ${reaction}`} onClick={() => void run(() => props.onReact(props.message.id, reaction), () => setReactionOpen(false))} disabled={pending}>{reaction}</button>)}</div>}
    {editing && <div className="contact-message-edit"><textarea aria-label="Новый текст сообщения" value={draft} onChange={event => setDraft(event.target.value)} rows={3} autoFocus /><div><button type="button" aria-label="Отменить редактирование" onClick={() => { setEditing(false); setError(''); }} disabled={pending}><X /> Отмена</button><button type="button" aria-label="Сохранить изменения" onClick={() => void run(() => props.onEdit(props.message.id, draft), () => setEditing(false))} disabled={pending || !draft.trim()}><Check /> Сохранить</button></div></div>}
    {props.failedAction && <div className="contact-action-failed"><span><b>Действие не выполнено</b><small>{props.failedAction.error || 'Внешний канал вернул ошибку'}</small></span><button type="button" aria-label="Повторить действие" onClick={() => void run(() => props.onRetry(props.failedAction!.id))} disabled={pending}><RefreshCw /> Повторить</button></div>}
    {error && <div className="contact-action-error" role="alert">{error}</div>}
  </div>;
}
